import moment from "moment/moment";
import { enqueueSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";
import { CoralogixRum } from "@coralogix/browser";

import {
  appUtilityKeys,
  contactStakeIfErrorPersistsMessage,
  ErrorToastVariant,
  InfoToastVariant,
  neglectEndpointsForUnauthorisedErrors,
  SuccessToastVariant,
  WarningToastVariant,
  weekdayNames,
} from "./constants";

import {
  CheckCircle,
  Cancel,
  Warning,
  Blitz,
  BankingOutline,
  Link,
  HourGlass,
  Clock,
  Recurring,
  Close,
  CheckCircleOutline,
  BlitzCircleFilled,
  Banking,
  Home,
  NotAllowed,
  Retry,
  Share,
  UpcomingSchedule,
} from "../Assets/Icons";

export const getNavItems = ({ hideDealsRoutes }) => [
  { Icon: Home, navItemName: "Home", navRoute: "/home" },
  { Icon: Banking, navItemName: "Banking", navRoute: "/banking" },
  ...(hideDealsRoutes ? [] : [{ Icon: BlitzCircleFilled, navItemName: "Deals", navRoute: "/deals" }]),
];

export const getToastVariantIcon = (type) => {
  switch (type) {
    case "success":
      return CheckCircle;
    case "error":
      return Cancel;
    case "warning":
      return Warning;
    case "info":
      return Warning;
    default:
      return () => <></>;
  }
};

export const getPayrollStatusIcon = (type) => {
  switch (type) {
    case "success":
      return CheckCircle;
    case "pending":
      return HourGlass;
    case "disconnected":
      return Link;
    default:
      return () => <></>;
  }
};

export const getRentPaymentStatusProps = (type) => {
  switch (type) {
    case "Failed":
      return { StatusIcon: Close, statusDisplayName: "Failed", statusType: "negative" };
    case "Rejected":
      return { StatusIcon: Retry, statusDisplayName: "Returned", statusType: "negative" };
    case "Pending":
      return { StatusIcon: Clock, statusDisplayName: "Pending", statusType: "positive" };
    case "InProgress":
      return { StatusIcon: Share, statusDisplayName: "Transfer Initiated", statusType: "positive" };
    case "Success":
      return { StatusIcon: CheckCircleOutline, statusDisplayName: "Success", statusType: "positive" };
    case "Canceled":
      return { StatusIcon: NotAllowed, statusDisplayName: "Canceled", statusType: "negative" };
    case "Upcoming":
      return { StatusIcon: UpcomingSchedule, statusDisplayName: "Upcoming", statusType: "positive" };
    case "Active":
      return { StatusIcon: Recurring, statusDisplayName: "Ongoing", statusType: "positive" };
    default:
      return () => <></>;
  }
};

export const getRentPaymentStatusItems = ({ isAutopay, paymentStatus } = {}) => {
  const paymentStatusFlags = {
    isScheduled: false,
    isTransferInitiated: false,
    isOngoing: false,
    isCancelled: false,
  };
  let StatusIcon = null;
  let statusBadgeName = "";
  let statusDisplayText = "";

  if (isAutopay && paymentStatus !== "Transfer Initiated") {
    paymentStatusFlags.isOngoing = true;
    StatusIcon = Recurring;
    statusBadgeName = "blue";
    statusDisplayText = "Autopay";
  } else {
    statusDisplayText = paymentStatus;
    switch (paymentStatus) {
      case "Scheduled":
        paymentStatusFlags.isScheduled = true;
        StatusIcon = Clock;
        statusBadgeName = "green";
        break;
      case "Transfer Initiated":
        paymentStatusFlags.isTransferInitiated = true;
        StatusIcon = CheckCircleOutline;
        statusBadgeName = "green";
        break;
      case "Cancelled":
        paymentStatusFlags.isCancelled = true;
        StatusIcon = Close;
        statusBadgeName = "red";
        break;
      default:
        break;
    }
  }

  return { paymentStatusFlags, statusBadgeName, statusDisplayText, StatusIcon };
};

export const isActiveModule = (modulePath, currentPath) => {
  return currentPath.startsWith(modulePath);
};

export const isOnboardingModule = (pathname) => {
  return ["/login", "/welcome", "/unclaimed", "/setup-account"].includes(pathname) || pathname.startsWith("/welcome/");
};

//Local Storage CRUD Helper

export const LocalStorage = {
  write: (key, val) => {
    if (val && typeof val === "object") localStorage.setItem(key, JSON.stringify(val));
    else localStorage.setItem(key, val);
  },
  read: (key) => {
    const str = localStorage.getItem(key);
    if (str === null) {
      return null;
    }

    // Check if the value is a boolean string ("true" or "false")
    if (str === "true" || str === "false") {
      return str === "true";
    }

    // Check if the value is a JSON object or array
    if (["{", "["].includes(str.charAt(0))) {
      try {
        return JSON.parse(str);
      } catch (e) {
        console.error("Failed to parse JSON:", e);
        return null;
      }
    }

    // If it's neither a boolean nor a JSON object, return the string as is
    return str;
  },
  remove: (key) => {
    localStorage.removeItem(key);
  },
  clear: () => localStorage.clear(),
};

//Toast Dispatch helpers

export const dispatchSuccessToast = (message, options = {}) =>
  enqueueSnackbar(message, { variant: SuccessToastVariant, key: uuidv4(), preventDuplicate: true, ...options });

export const dispatchErrorToast = (message, options = {}) =>
  enqueueSnackbar(message ?? "Oops! Something went wrong!", {
    variant: ErrorToastVariant,
    key: uuidv4(),
    preventDuplicate: true,
    ...options,
  });

export const dispatchAPIErrorToast = ({ message, ...options } = {}) =>
  dispatchErrorToast(!message ? contactStakeIfErrorPersistsMessage : message, options);

export const dispatchWarningToast = (message, options = {}) =>
  enqueueSnackbar(message, { variant: WarningToastVariant, key: uuidv4(), preventDuplicate: true, ...options });

export const dispatchInfoToast = (message, options = {}) =>
  enqueueSnackbar(message, { variant: InfoToastVariant, key: uuidv4(), preventDuplicate: true, ...options });

//Data Tranformation helpers

export const replaceNonNumericCharacters = (number) => number?.replace(/[^\d]/gi, "");

export const transformUSNumberToCountryCodeFormat = (number) => `+1${replaceNonNumericCharacters(number)}`;

export const removeCountryCodeFromPhoneNumber = (number) => number.replace("+1", "");

export const transformCountryCodeFormatToUSNumber = (number) => {
  var cleaned = removeCountryCodeFromPhoneNumber(number).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return "";
};

export const getNumberFromCurrentAmount = (a) => Number(a.replace(/[^0-9.-]+/g, ""));

export const getMaskedSSN = (ssn) => {
  const nonFormattedSSN = replaceNonNumericCharacters(ssn);
  const len = nonFormattedSSN.length;
  const stars =
    len > 0 ? (len > 1 ? (len > 2 ? (len > 3 ? (len > 4 ? "••• •• " : "••• •") : "••• ") : "••") : "•") : "";
  return stars + nonFormattedSSN.substring(5);
};

export const getFormattedSSN = (ssn) => {
  const numericSSN = replaceNonNumericCharacters(ssn);
  return `${numericSSN.substring(0, 3)} ${numericSSN.substring(3, 5)} ${numericSSN.substring(5, 9)}`;
};

/*eslint no-control-regex: "off"*/
export const removeControlCharacters = (str) => str.replace(/[^\x00-\x7F]/g, "");

//API Request config builder

export const getAPIConfig = (builder = []) => {
  const headers = new Headers();

  if (builder.includes("authKey")) {
    const authKey = LocalStorage.read(appUtilityKeys.authKey);
    headers.append("auth", authKey);
  }

  if (builder.includes("xAccessToken")) {
    const authKey = LocalStorage.read(appUtilityKeys.authKey);
    headers.append("x-access-token", authKey);
  }

  if (builder.includes("transferToken")) {
    const transferToken = LocalStorage.read(appUtilityKeys.transferToken);
    headers.append("auth", transferToken);
  }

  const user = LocalStorage.read(appUtilityKeys.loggedInUserData);

  if (builder.includes("userId") && !!user) {
    headers.append("userId", user._id ?? "");
  }

  if (builder.includes("user") && !!user) {
    headers.append("user", user._id ?? "");
  }

  if (builder.includes("userEmail") && !!user) {
    headers.append("user-email", user.email ?? "");
  }

  if (builder.includes("astraToken")) {
    const astraToken = LocalStorage.read(appUtilityKeys.astraToken) ?? "";
    headers.append("Authorization", astraToken);
  }

  if (builder.includes("formData")) {
    headers.append("Content-Type", "multipart/form-data");
  }

  if (builder.includes("stakePay3AuthToken")) {
    const authToken = LocalStorage.read(appUtilityKeys.stakePay3AuthToken);
    headers.append("Token", authToken);
  }

  return {
    headers: Object.fromEntries(headers.entries()),
  };
};

//Object Attribute Inject helper

export const injectAttributes = (obj, builder = []) => {
  const user = LocalStorage.read(appUtilityKeys.loggedInUserData);

  if (builder.includes("userId") && user) {
    obj.userId = user._id;
  }

  return obj;
};

//API Interceptors
export const CoreAPIRequestInterceptor = (req) => {
  req.headers["dealsAPIKey"] = process.env.REACT_APP_CORE_API_KEY;
  return req;
};

export const APIErrorInterceptor = (err) => {
  if (!neglectEndpointsForUnauthorisedErrors.find((endpoint) => err?.request?.responseURL.endsWith(endpoint))) {
    if ([401, 403].find((e) => e === err?.response?.status)) {
      onSignOut();
      return;
    }
  }
  throw err;
};

//Formatting helpers
export const capitaliseString = (str) => {
  return (str.charAt(0) ?? "").toUpperCase() + (str.slice(1) ?? "").toLowerCase();
};

export const formatNumber = (number, decimalPoints = 2) => {
  if (!isNaN(number)) {
    return new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: decimalPoints,
      maximumFractionDigits: decimalPoints,
    }).format(number);
  }
  return null;
};

export const formatCurrency = (number, decimalPoints = 2) => {
  if (!isNaN(number)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimalPoints,
      maximumFractionDigits: decimalPoints,
    }).format(number);
  }
  return null;
};

export const formatNumberWithCentsToUSD = (amount) => {
  // Divide by 100 to move the last two digits into cents
  return formatCurrency(amount / 100);
};

export const formatDate = (dateString, options = {}) => {
  if (!!dateString && !isNaN(new Date(dateString))) {
    return new Intl.DateTimeFormat("en-US", options).format(new Date(dateString));
  }
  return null;
};

export const getISOFormatDate = (date) => date?.toISOString().split("T")[0];

export const appendISOTime = (date) => `${date}T00:00:00`;

export const normalizeDateToMidnight = (date = new Date()) => {
  const normalizeDate = new Date(new Date(date).setHours(0, 0, 0, 0));
  return normalizeDate;
};

//SVD Status Checkers

export const isSVDApproved = (stakeDebit) => {
  return (
    !!stakeDebit &&
    (stakeDebit?.data?.status === "ApplicationApproved" ||
      stakeDebit?.data?.status === "DepositAccountCreated" ||
      stakeDebit?.data?.status === "DebitCardRequested" ||
      stakeDebit?.data?.status === "DebitCardActivated")
  );
};

export const isSVDPendingVerification = (stakeDebit) => {
  return (
    !!stakeDebit &&
    (stakeDebit?.data?.status === "VerificationRequired" ||
      stakeDebit?.data?.status === "ApplicationPendingAddressVerification" ||
      stakeDebit?.data?.status === "ApplicationPendingIDVerification" ||
      stakeDebit?.data?.status === "ApplicationPendingSSNVerification")
  );
};

export const isSVDVerificationRequired = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "VerificationRequired";
};

export const isSVDPending = (stakeDebit) => {
  return !!stakeDebit && ["ApplicationPending", "VerificationPending"].includes(stakeDebit?.data?.status);
};

export const isSVDReview = (stakeDebit) => {
  return !!stakeDebit && ["VerificationSubmitted", "VerificationUnderReview"].includes(stakeDebit?.data?.status);
};

export const hasSVDVirtualCard = (stakeDebit) => {
  return (
    !!stakeDebit &&
    !!stakeDebit?.data?.data?.virtual_card_id &&
    !!stakeDebit?.data?.data?.virtual_card_expiration &&
    !!stakeDebit?.data?.data?.virtual_card_last_4
  );
};

export const hasSVDPhysicalCard = (stakeDebit) => {
  return !!stakeDebit && ["DebitCardRequested", "DebitCardActivated"].includes(stakeDebit?.data?.status);
};

export const isSVDCardRequested = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "DebitCardRequested";
};

export const isSVDDebitCardActivated = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "DebitCardActivated";
};

export const isSVDEligible = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "Eligible";
};

export const isSVDDenied = (stakeDebit) => {
  return !!stakeDebit && ["ApplicationDenied", "VerificationRejected"].includes(stakeDebit?.data?.status);
};

export const isSVDIneligible = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "Ineligible";
};

export const isSVDClosedForStakeSaverNonCompliance = (stakeDebit) => {
  return !!stakeDebit && stakeDebit?.data?.status === "ClosedForStakeSaverNonCompliance";
};

export const hasSVDPhysicalCardArrived = (arrivalDate) => {
  return new Date().getTime() >= arrivalDate?.getTime();
};

//Date time helpers

export function getThirdTuesdayOfMonth(y, m) {
  let date = new Date(y, m, 1);
  let dayOfWeek = date.getDay();
  let daysUntilFirstTuesday = (9 - dayOfWeek) % 7;
  date.setDate(1 + daysUntilFirstTuesday + 14); // 14 days after the first Tuesday
  return date;
}

export const getThirdTuesdayOfMonthCached = (() => {
  const cache = new Map();

  return (y, m) => {
    const key = `${y}-${m}`;
    if (cache.has(key)) {
      return cache.get(key);
    }

    let thirdTuesdayDate = getThirdTuesdayOfMonth(y, m);
    cache.set(key, thirdTuesdayDate);

    return thirdTuesdayDate;
  };
})();

export const isThirdTuesdayOfMonth = (d, m, y) => {
  const thirdTuesdayOfMonth = getThirdTuesdayOfMonthCached(y, m);
  return d === thirdTuesdayOfMonth.getDate();
};

export function getNextThirdTuesday() {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth();

  // Function to get the third Tuesday of a given month and year
  let thirdTuesday = getThirdTuesdayOfMonth(year, month);

  // If the third Tuesday has passed, get the third Tuesday of next month
  if (thirdTuesday < now) {
    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
    thirdTuesday = getThirdTuesdayOfMonth(year, month);
  }

  return thirdTuesday;
}

//Misc helpers

export const onSignOut = ({ redirectOnLogout = true } = {}) => {
  LocalStorage.remove(appUtilityKeys.authKey);
  LocalStorage.remove(appUtilityKeys.loggedInUserData);
  LocalStorage.remove(appUtilityKeys.userSfid);
  LocalStorage.remove(appUtilityKeys.transferToken);
  LocalStorage.remove(appUtilityKeys.astraToken);
  LocalStorage.remove(appUtilityKeys.astraRefreshToken);
  LocalStorage.remove(appUtilityKeys.astraIntentId);
  LocalStorage.remove(appUtilityKeys.lastAstraTokenFetchTime);
  LocalStorage.remove(appUtilityKeys.astraSelectedCardId);
  LocalStorage.remove(appUtilityKeys.stakePay3AuthToken);

  if (!!window.uxc) {
    window.uxc.clearSession();
  }

  if (!!window.heap) {
    window.heap.resetIdentity();
  }

  if (CoralogixRum.isInited) {
    CoralogixRum.shutdown();
  }

  if (redirectOnLogout) {
    window.location.pathname = "/login";
  }
};

export const getHasVisitedWelcomeScreenKey = () => {
  const userId = LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;
  return appUtilityKeys.hasVisitedWelcomeScreen(userId);
};

export const getAnimateAvailableBalanceFromKey = () => {
  const userId = LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;
  return appUtilityKeys.animateAvailableBalanceFrom(userId);
};

export const getUserSfid = () => LocalStorage.read(appUtilityKeys.userSfid);

export const getUserId = () => LocalStorage.read(appUtilityKeys.loggedInUserData)?._id;

export const getUserEmail = () => LocalStorage.read(appUtilityKeys.loggedInUserData)?.email;

export const getTransferTypes = (dealsData) => {
  let instantDescription = `In a few minutes\n`;
  if (!(dealsData?.data?.currentDeal?.has_used_1_free_instant_transfer__c ?? true)) {
    instantDescription += `${
      dealsData?.data?.currentDeal?.has_used_1_free_instant_transfer__c ? 0 : 1
    } free use remaining`;
  }

  return [
    ...(dealsData?.data?.currentDeal?.enabled_for_instant_transfer__c
      ? [
          {
            type: "instant",
            Icon: Blitz,
            title: "Instant",
            description: instantDescription,
          },
        ]
      : []),
    {
      type: "ach",
      Icon: BankingOutline,
      title: "1-3 biz days",
      description: `Estimated by ${dealsData?.content?.transaction_clearing_date}`,
    },
  ];
};

export const saveAndUpdateUserOnLogin = (data) => {
  LocalStorage.write(appUtilityKeys.authKey, data?.data?.[appUtilityKeys.authKey]);
  LocalStorage.write(appUtilityKeys.loggedInUserData, data?.data?.[appUtilityKeys.loggedInUserData]);

  if (!!window.uxc) {
    window.uxc.setUserIdentity(data?.data?.user?.phoneNumber ?? "");
    if (!data.data.user.phoneNumber) {
      window.uxc.setUserIdentity(data?.data?.user?.email ?? "");
    }

    window.uxc.setUserProperties({
      user_id: data?.data?.user?._id ?? "",
    });
  }

  if (!!window.heap) {
    window.heap.identify(data?.data?.user?.email ?? "");
  }

  if (CoralogixRum.isInited) {
    CoralogixRum.setUserContext({
      user_id: data?.data?.user?._id,
      user_email: data?.data?.user?.email,
      user_metadata: { phone_number: data?.data?.user?.phoneNumber },
    });
  }
};

export const isSelectedTransferMethodBank = (selectedMethod) => selectedMethod === "ach";
export const isSelectedTransferMethodInstant = (selectedMethod) => selectedMethod === "instant";

export const isSelectedAutomaticFundingMethodExpressPaycheck = (selectedAutomaticFundingMethod) =>
  selectedAutomaticFundingMethod === "express-paycheck";
export const isSelectedAutomaticFundingMethodDirectDeposit = (selectedAutomaticFundingMethod) =>
  selectedAutomaticFundingMethod === "direct-deposit";

export const getIsDebitCardExpired = (expiryMonthYear) => {
  var creditCardDate = moment(expiryMonthYear, "MM/YY");
  var today = moment();

  return !creditCardDate.isValid() || today >= creditCardDate.add(1, "months");
};

export const getPublicIPAddress = async () => {
  try {
    const response = await fetch("https://checkip.amazonaws.com/");
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const ip = await response.text();
    return ip.trim(); // Trim to remove any extra spaces or new lines
  } catch (error) {
    console.error("Error fetching the IP address:", error);
    return null;
  }
};

export const formatBirthDate = (birthDate) => {
  // If birthDate is empty, return an empty string
  if (!birthDate) {
    return "";
  }

  // Parse the input date string to a Date object
  const date = new Date(birthDate);

  // Check if the date is valid
  if (isNaN(date)) {
    return "1990-01-01"; // Fallback to default date
  }

  // Format the date to 'yyyy-MM-dd' (e.g., 2023-11-05)
  const outputDate = getISOFormatDate(date);
  return outputDate;
};

export const extract5DigitZipCode = (zipCode) => {
  if (!zipCode) {
    return null;
  }
  const match = zipCode.match(/^\d{5}/);
  return match ? match[0] : null;
};

export const getAstraCardsWithSystemCardFiltered = (astraCards, stakeDebit) => {
  const unitLast4 = stakeDebit?.data?.data?.card_last_4;
  const virtualUnitLast4 = stakeDebit?.data?.data?.virtual_card_last_4;

  return (
    astraCards?.filter((card) => card.last_four_digits !== unitLast4 && card.last_four_digits !== virtualUnitLast4) ??
    []
  );
};

export const getSelectedAstraCard = (astraCards, stakeDebit, preferredCardId = null) => {
  const filteredOutCards = getAstraCardsWithSystemCardFiltered(astraCards, stakeDebit);

  if (filteredOutCards.length > 0) {
    const selectedId = !!preferredCardId ? preferredCardId : null;
    const foundSelectedCard = filteredOutCards.filter((card) => card.id === selectedId);
    if (!!selectedId && foundSelectedCard.length > 0) {
      return foundSelectedCard[0];
    } else {
      return filteredOutCards[0];
    }
  } else {
    return null;
  }
};

export const getActiveSVDAstraDebitCard = (astraCards, stakeDebit) => {
  const unitLast4 = stakeDebit?.data?.data?.card_last_4 ?? "";
  const virtualUnitLast4 = stakeDebit?.data?.data?.virtual_card_last_4 ?? "";

  const unitCard = astraCards?.find((card) => card.last_four_digits === unitLast4);
  if (unitCard) {
    return unitCard;
  }

  const virtualCard = astraCards?.find((card) => card.last_four_digits === virtualUnitLast4);
  if (virtualCard) {
    return virtualCard;
  }

  return null;
};

export const getRecurringDepositDefaultPreferences = () => {
  return { startDate: getISOFormatDate(moment().add(1, "days")), frequency: "Weekly" };
};

export const getNumberOrdinal = (num) => {
  const ords = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return ords[(v - 20) % 10] || ords[v] || ords[0];
};

export const getNumberWithOrdinal = (num) => {
  return `${num}${getNumberOrdinal(num)}`;
};

export const convertObjectToFormData = (obj) => {
  const formData = new FormData();
  Object.entries(obj).forEach(([k, v]) => formData.append(k, v));
  return formData;
};

export const zPad = (n, width, z) => {
  z = z || "0";
  n = n + "";
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
};

export function getAcronym(text) {
  if (!text) return "";
  return text
    .trim()
    .split(/\s+/)
    .map((word) => word[0] || "")
    .join("")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase();
}

export function getRecurringStartDate(frequency, recurringDay, fromDate = normalizeDateToMidnight()) {
  const date = new Date(fromDate);

  if (!!recurringDay) {
    if (frequency === "weekly" || frequency === "biweekly") {
      const targetDay = weekdayNames.indexOf(capitaliseString(recurringDay)) + 1;
      let diff = targetDay - date.getDay();
      if (diff <= 0) diff += 7;
      date.setDate(date.getDate() + diff);
      return date;
    }

    if (frequency === "monthly") {
      const day = Number(recurringDay);
      const next = new Date(date.getFullYear(), date.getMonth(), day);
      if (next <= date) {
        return new Date(date.getFullYear(), date.getMonth() + 1, day);
      }
      return next;
    }
  }

  return null;
}

export function getRecurringEndDate({ frequency, startDate, durationType, noOfPayments, specificDate }) {
  if (durationType === "until i cancel") {
    return null;
  }
  if (durationType === "until specific date") {
    const endLimit = normalizeDateToMidnight(specificDate);
    let date = normalizeDateToMidnight(startDate);
    if (date > endLimit) return null;
    if (frequency === "weekly" || frequency === "biweekly") {
      const interval = frequency === "weekly" ? 7 : 14;
      while (true) {
        const next = new Date(date);
        next.setDate(next.getDate() + interval);
        if (next > endLimit) break;
        date = next;
      }
      return date;
    } else if (frequency === "monthly") {
      const startDate = date.getDate();
      let month = date.getMonth();
      let year = date.getFullYear();
      while (true) {
        const next = new Date(year, month + 1, startDate);
        if (next > endLimit) break;
        date = next;
        month++;
      }
      return date;
    }
  }
  if (durationType === "until specific number of payments") {
    if (!noOfPayments) return null;
    const date = normalizeDateToMidnight(startDate);
    for (let i = 1; i < noOfPayments; i++) {
      if (frequency === "weekly") {
        date.setDate(date.getDate() + 7);
      } else if (frequency === "biweekly") {
        date.setDate(date.getDate() + 14);
      } else if (frequency === "monthly") {
        date.setMonth(date.getMonth() + 1);
      }
    }
    return date;
  }

  return null;
}

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getStepCount = (step) => {
  return <span className="step-count">{step}</span>;
};

export const getStepIcon = (icon) => {
  return (
    <div className="step-icon-wrapper">
      <span className="step-icon">{icon}</span>
    </div>
  );
};
